/**
 * PendingDocumentsCard — "Pendências" da home, uma linha por TIPO de documento.
 *
 * ─── AS DUAS DECISÕES DE DESENHO ────────────────────────────────────────────
 *
 * **1. Uma linha por TIPO, nunca por documento.** Listar os documentos
 * pendentes faria o card crescer sem teto e duplicaria a lista de "Últimos
 * documentos" logo abaixo. Agrupado, o card tem no máximo CINCO linhas —
 * orçamento, contrato, OS, financeiro e nota — e encolhe sozinho para quem não
 * usa metade dos módulos, porque grupo com contagem zero é omitido
 * (`buildPendingSummary`).
 *
 * **2. O rótulo é AUTOEXPLICATIVO, e por isso a linha é única.** "Orçamentos
 * sem resposta" cabe em 360px e dispensa subtítulo; um par rótulo+subtítulo
 * dobraria a altura de cada linha (5 × 44px viraria 5 × 76px) para dizer o
 * mesmo. Os rótulos moram no módulo puro justamente para não divergirem se um
 * dia outra tela mostrar o mesmo resumo.
 *
 * As CORES DO TIPO são as mesmas dos atalhos e da lista de documentos: laranja
 * = ordem de serviço, violeta = contrato, verde = financeiro, índigo = nota.
 * É o que liga a linha ao ícone pelo qual a pessoa chegaria no módulo.
 *
 * O card **não aparece** quando não há pendência nenhuma: um "tudo em dia" fixo
 * ocuparia a dobra mais cara da tela para não dizer nada. Quem carrega a
 * ausência de trabalho é o estado vazio da lista de documentos.
 */
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  ClipboardList,
  DollarSign,
  FileSignature,
  FileText,
  Receipt,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { CONTENT_CARD } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import { plural } from '@/lib/plural';
import { STALE_QUOTE_DAYS, type PendingKind, type PendingSummary } from '@/lib/pendingDocuments';

/** Ícone e cor por tipo — espelham os atalhos da grade acima. */
const KIND_VISUALS: Record<PendingKind, { icon: LucideIcon; className: string }> = {
  quote: { icon: FileText, className: 'bg-primary/10 text-primary' },
  contract: { icon: FileSignature, className: 'bg-violet-500/10 text-violet-600' },
  service_order: { icon: ClipboardList, className: 'bg-orange-500/10 text-orange-600' },
  finance: { icon: DollarSign, className: 'bg-green-500/10 text-green-600' },
  nfse: { icon: Receipt, className: 'bg-indigo-500/10 text-indigo-600' },
};

interface PendingDocumentsCardProps {
  summary: PendingSummary;
}

export function PendingDocumentsCard({ summary }: PendingDocumentsCardProps) {
  const navigate = useNavigate();

  if (summary.total === 0) return null;

  return (
    <div className={cn(CONTENT_CARD, 'overflow-hidden')}>
      <div className="flex items-center justify-between px-4 pb-2 pt-3">
        <h2 className="text-sm font-semibold">Pendências</h2>
        {/* `plural`: "1 itens" foi defeito real neste app (#87). */}
        <span className="text-xs text-muted-foreground">
          {plural(summary.total, 'item', 'itens')}
        </span>
      </div>

      <ul className="divide-y divide-border/60">
        {summary.groups.map((group) => {
          const visual = KIND_VISUALS[group.kind];
          const Icon = visual.icon;
          return (
            <li key={group.kind}>
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-muted/60"
                onClick={() => navigate(group.route)}
              >
                <span
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                    visual.className,
                  )}
                >
                  <Icon className="h-4 w-4" />
                </span>

                {/* `min-w-0` obrigatório: sem ele o item flex nasce com
                    `min-width: auto` e o rótulo empurra a linha para fora da
                    tela em vez de truncar (#175/#185).

                    ⚠️ O ATRASO VAI EM LINHA PRÓPRIA, e isso foi MEDIDO no
                    aparelho: com o selo na mesma linha, sobravam ~114px para o
                    rótulo e "Orçamentos sem resposta" precisa de ~160 — a tela
                    imprimia "Orçamentos se…", cortando justamente a palavra que
                    diz o que está pendente (#52/#175). Sem o selo ao lado, o
                    rótulo tem ~192px e cabe inteiro; e como só orçamento e
                    financeiro têm noção de atraso, a segunda linha aparece em
                    no máximo duas das cinco. */}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{group.label}</span>
                  {/* O atraso é o que diferencia "tem coisa em aberto" de "tem
                      coisa PARADA" — e é a única razão de o card ser urgente. */}
                  {group.overdueCount > 0 && (
                    <span className="block truncate text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                      {group.kind === 'quote'
                        ? `${plural(group.overdueCount, 'parado', 'parados')} há mais de ${STALE_QUOTE_DAYS} dias`
                        : plural(group.overdueCount, 'vencida', 'vencidas')}
                    </span>
                  )}
                </span>

                <span className="shrink-0 text-sm font-bold tabular-nums">{group.count}</span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
